/**
 * RouteBdr — /#/leads/:id/route-bdr
 *
 * Step 2 of the Lead-to-AE wizard (P0-5).
 * Assigns a freshly-created lead to a BDR for outbound qualification.
 *
 * The BDR will see this lead in their qualification queue and run M-PACT
 * discovery (Step 3 — /#/leads/:id/qualify). Only after the BDR marks
 * the lead Qualified will it become eligible for AE assignment.
 *
 * Override path: if the lead was created with "Skip BDR" enabled, this
 * screen is bypassed entirely; AddLead routes directly to /assign.
 */

import { useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/session";
import { RibbonProgress } from "./RibbonProgress";

type BdrUser = { id: string; name: string; email: string };

export default function RouteBdr() {
  const params = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const prospectId = params.id;

  const [prospect, setProspect] = useState<any>(null);
  const [bdrs, setBdrs] = useState<BdrUser[]>([]);
  const [selectedBdr, setSelectedBdr] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [routing, setRouting] = useState(false);

  useEffect(() => {
    if (!prospectId) return;
    let cancelled = false;
    async function load() {
      try {
        const [p, list] = await Promise.all([
          api<any>(`/api/prospects/${prospectId}`),
          api<BdrUser[]>("/api/bdr-users"),
        ]);
        if (cancelled) return;
        setProspect(p);
        setBdrs(list);
        if (list.length > 0) setSelectedBdr(list[0].id);
      } catch (e: any) {
        if (!cancelled) {
          toast({
            title: "Could not load BDRs",
            description: e?.message ?? "Check your connection.",
            variant: "destructive",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [prospectId]);

  async function handleRoute() {
    if (!selectedBdr || routing) return;
    setRouting(true);
    try {
      await api<{ ok: boolean }>(`/api/prospects/${prospectId}/route-bdr`, {
        method: "POST",
        body: JSON.stringify({ bdrUuid: selectedBdr }),
      });
      // After routing, the BDR sees the lead in their queue. We continue the
      // wizard to the qualification screen so the demo flow stays linear.
      navigate(`/leads/${prospectId}/qualify`);
    } catch (e: any) {
      toast({
        title: "Could not route to BDR",
        description: e?.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setRouting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="sticky top-0 z-40 bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-5 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/leads/new")}
              className="p-1.5 rounded-md text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 transition-colors"
              aria-label="Back"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
                <path d="M11 13.5L6.5 9 11 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 leading-none mb-0.5">Mi6 Attilius</div>
              <div className="text-sm font-bold text-[#0B0B0F] leading-none">
                Route to BDR
                {prospect && (
                  <span className="ml-1.5 font-normal text-neutral-500">
                    — {prospect.company}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-[10px] text-neutral-500">Step 2 of 5</div>
        </div>
        <RibbonProgress step={2} />
      </header>

      <main className="max-w-2xl mx-auto px-5 py-8">
        <div className="mb-7">
          <h1 className="text-[22px] font-bold text-[#0B0B0F] tracking-tight">Route to a BDR</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Assign this lead to a Business Development Representative for outbound
            qualification. The BDR will run M-PACT discovery to confirm methodology fit,
            process clarity, accountability, coaching access, and timeline urgency before the lead is handed to an AE.
          </p>
        </div>

        {/* Prospect summary */}
        {prospect && (
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-5 mb-5">
            <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 mb-2">Lead</div>
            <div className="text-base font-bold text-[#0B0B0F]">{prospect.company}</div>
            <div className="text-sm text-neutral-600">
              {prospect.full_name}
              {prospect.title ? ` · ${prospect.title}` : ""}
            </div>
            {prospect.source && (
              <div className="text-[11px] text-neutral-500 mt-2">
                Source: <span className="font-medium text-neutral-700">{String(prospect.source).replace(/_/g, " ")}</span>
                {prospect.marketing_event_id && (
                  <> · Campaign <code className="font-mono">{prospect.marketing_event_id}</code></>
                )}
              </div>
            )}
          </div>
        )}

        {/* BDR picker */}
        <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6">
          <label className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-1.5">
            Assign to BDR
          </label>
          {loading ? (
            <div className="text-sm text-neutral-500">Loading BDRs…</div>
          ) : bdrs.length === 0 ? (
            <div className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded-lg px-3 py-2">
              No BDRs are set up in this tenant. Ask an admin to create a BDR user, or
              use the “Override — skip BDR” toggle on the Add Lead screen.
            </div>
          ) : (
            <select
              className="w-full rounded-lg border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-800 focus:border-[#0B0B0F] focus:ring-2 focus:ring-neutral-200/60 outline-none transition-all cursor-pointer"
              value={selectedBdr}
              onChange={(e) => setSelectedBdr(e.target.value)}
              data-testid="bdr-select"
            >
              {bdrs.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} — {b.email}
                </option>
              ))}
            </select>
          )}
          <p className="text-[11px] text-neutral-500 mt-2">
            The lead will appear in the BDR’s qualification queue at <code className="font-mono">/leads/:id/qualify</code>.
          </p>
        </div>

        {/* CTAs */}
        <div className="mt-7 flex items-center gap-3">
          <button
            type="button"
            onClick={handleRoute}
            disabled={!selectedBdr || routing}
            className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0B0B0F] text-white text-sm font-bold tracking-wide hover:bg-[#374151] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-150 shadow-sm"
            data-testid="route-to-bdr"
          >
            {routing ? "Routing…" : "Route to BDR"}
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => navigate("/dashboards/bdr")}
            className="px-4 py-3 rounded-xl border border-neutral-200 text-sm font-semibold text-neutral-600 hover:bg-neutral-50 transition-colors"
          >
            Save and exit
          </button>
        </div>

        <div className="mt-5 text-[11px] text-neutral-500 leading-relaxed">
          <strong className="text-neutral-700">Why this step?</strong> The Mi6 methodology runs the
          MQL → BDR → SQL → AE funnel. Skipping the BDR collapses two distinct
          motions (outbound qualification and AE selling) into one and reduces
          conversion fidelity. Use the explicit override on Add Lead only when an AE
          has already self-sourced and qualified the lead.
        </div>
      </main>
    </div>
  );
}
